import type { ListingDetailV3PetPolicy } from '@/src/lib/api/endpoints/listing';
import { combinedAllowed, groupPetPoliciesByKind, policiesForPet } from '@/src/lib/utils/petKind';
import { classifyAmenity, isExcludedAmenity } from './utils/amenityGrouping';

export type AmenityPreviewItem =
  | { kind: 'parking'; status: 'available' | 'unavailable' }
  | { kind: 'pets'; status: 'friendly' | 'dogFriendly' | 'catFriendly' | 'unavailable' }
  | { kind: 'laundry'; status: 'inUnit' | 'shared' | 'unavailable' }
  | { kind: 'airConditioning' }
  | { kind: 'security' }
  | { kind: 'other'; id: string; label: string };

export type AmenityHighlight = {
  id: string;
  display: string;
  types: Array<string>;
};

export type AmenityPetPolicy = Pick<ListingDetailV3PetPolicy, 'petType' | 'petSize' | 'allowed'>;

export type V3AmenitiesPreviewInput = {
  building?: Array<string>;
  unit?: Array<string>;
  petPolicies?: Array<AmenityPetPolicy>;
};

const MAX_PREVIEW_ITEMS = 5;
const EXCLUDED_LEGACY_HIGHLIGHT_IDS = new Set(['appliances', 'outdoorAreas']);
const NO_PARKING_PATTERN = /\bno parking\b/i;
const LAUNDRY_PATTERN = /\b(laundry|washer|dryer)\b/i;
const NO_LAUNDRY_PATTERN = /\bno laundry\b|^laundry:\s*none\b/i;
const IN_UNIT_LAUNDRY_PATTERN = /\b(in[- ]unit|washer|dryer)\b/i;
const AIR_CONDITIONING_PATTERN = /\b(air conditioning|a\/c|cooling)\b/i;

type V3PreviewCategory = 'parking' | 'laundry' | 'airConditioning' | 'security';

function amenityId(label: string): string {
  return label
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
}

function classifyV3Amenity(label: string): V3PreviewCategory | null {
  const classification = classifyAmenity(label);
  if (classification?.subGroup === 'Parking') return 'parking';
  if (LAUNDRY_PATTERN.test(label)) return 'laundry';
  if (classification?.subGroup === 'Climate' && AIR_CONDITIONING_PATTERN.test(label)) return 'airConditioning';
  if (classification?.subGroup === 'Security') return 'security';
  return null;
}

function buildPetsPreview(policies: Array<AmenityPetPolicy> | undefined): AmenityPreviewItem | null {
  const byKind = groupPetPoliciesByKind((policies ?? []).map((policy) => ({ petType: policy.petType, policy })));
  const catAllowed = combinedAllowed(policiesForPet(byKind, 'cat'));
  const dogAllowed = combinedAllowed(policiesForPet(byKind, 'dog'));

  if (catAllowed && dogAllowed) return { kind: 'pets', status: 'friendly' };
  if (catAllowed) return { kind: 'pets', status: 'catFriendly' };
  if (dogAllowed) return { kind: 'pets', status: 'dogFriendly' };
  if (catAllowed === false && dogAllowed === false) return { kind: 'pets', status: 'unavailable' };
  return null;
}

function buildLegacyPriorityItem(highlight: AmenityHighlight): AmenityPreviewItem | null {
  const text = highlight.types.join(' ');

  if (highlight.id === 'parking') {
    return { kind: 'parking', status: NO_PARKING_PATTERN.test(text) ? 'unavailable' : 'available' };
  }
  if (highlight.id === 'pets') {
    if (/\bno pets allowed\b/i.test(text)) return { kind: 'pets', status: 'unavailable' };
    const hasCats = /\bcat/i.test(text);
    const hasDogs = /\bdog/i.test(text);
    if (hasCats && hasDogs) return { kind: 'pets', status: 'friendly' };
    if (hasCats) return { kind: 'pets', status: 'catFriendly' };
    if (hasDogs) return { kind: 'pets', status: 'dogFriendly' };
    return null;
  }
  if (highlight.id === 'laundry') {
    if (NO_LAUNDRY_PATTERN.test(text)) return { kind: 'laundry', status: 'unavailable' };
    if (/\bin unit\b/i.test(text)) return { kind: 'laundry', status: 'inUnit' };
    if (/\bshared\b/i.test(text)) return { kind: 'laundry', status: 'shared' };
    return null;
  }
  if (highlight.id === 'hvac' && AIR_CONDITIONING_PATTERN.test(text)) return { kind: 'airConditioning' };
  if (highlight.id === 'security') return { kind: 'security' };
  return null;
}

/**
 * Normalizes the two active HDP amenity sources into one presentation model.
 * Building HDPs still use persisted legacy highlights; unit HDPs use V3 string
 * arrays and pet policies. Delete the legacy builder when building HDPs move to V3.
 */
export function buildLegacyAmenitiesPreview(highlights: Array<AmenityHighlight>): Array<AmenityPreviewItem> {
  const priorityIds = ['parking', 'pets', 'laundry', 'hvac', 'security'];
  const priorityItems = priorityIds.flatMap<AmenityPreviewItem>((id) => {
    const highlight = highlights.find((candidate) => candidate.id === id);
    if (!highlight) return [];
    const item = buildLegacyPriorityItem(highlight);
    if (item) return [item];
    return highlight.display ? [{ kind: 'other', id: highlight.id, label: highlight.display }] : [];
  });
  const fallbackItems = highlights
    .filter(
      (highlight) =>
        !priorityIds.includes(highlight.id) &&
        !EXCLUDED_LEGACY_HIGHLIGHT_IDS.has(highlight.id) &&
        Boolean(highlight.display),
    )
    .map<AmenityPreviewItem>((highlight) => ({ kind: 'other', id: highlight.id, label: highlight.display }));

  return [...priorityItems, ...fallbackItems].slice(0, MAX_PREVIEW_ITEMS);
}

export function buildV3AmenitiesPreview(input: V3AmenitiesPreviewInput): Array<AmenityPreviewItem> {
  const amenities = [...(input.building ?? []), ...(input.unit ?? [])].filter((amenity) => !isExcludedAmenity(amenity));
  const categorized = amenities.map((label) => ({ label, category: classifyV3Amenity(label) }));
  const parking = categorized.filter(({ category }) => category === 'parking').map(({ label }) => label);
  const laundry = categorized.filter(({ category }) => category === 'laundry').map(({ label }) => label);
  const priorityItems: Array<AmenityPreviewItem> = [];

  if (parking.length > 0) {
    priorityItems.push({
      kind: 'parking',
      status: parking.some((label) => !NO_PARKING_PATTERN.test(label)) ? 'available' : 'unavailable',
    });
  }

  const pets = buildPetsPreview(input.petPolicies);
  if (pets) priorityItems.push(pets);

  if (laundry.length > 0) {
    const text = laundry.join(' ');
    const status = IN_UNIT_LAUNDRY_PATTERN.test(text)
      ? 'inUnit'
      : laundry.some((label) => !NO_LAUNDRY_PATTERN.test(label))
        ? 'shared'
        : 'unavailable';
    priorityItems.push({ kind: 'laundry', status });
  }

  if (categorized.some(({ category }) => category === 'airConditioning')) {
    priorityItems.push({ kind: 'airConditioning' });
  }
  if (categorized.some(({ category }) => category === 'security')) {
    priorityItems.push({ kind: 'security' });
  }

  const seenFallbackIds = new Set<string>();
  const fallbackItems = categorized.flatMap<AmenityPreviewItem>(({ label, category }) => {
    if (category) return [];
    const id = amenityId(label);
    if (!id || seenFallbackIds.has(id)) return [];
    seenFallbackIds.add(id);
    return [{ kind: 'other', id, label }];
  });

  return [...priorityItems, ...fallbackItems].slice(0, MAX_PREVIEW_ITEMS);
}
